<template>
  <div class="scoreboard-wrapper bg-dark text-white shadow-up-1">
    <div class="row no-wrap text-center text-caption items-center">
      <!-- TEAMS -->
      <!-- FIX: Added 'ellipsis' and 'full-width' so long names don't break the column width -->
      <div class="col-3 column border-right">
        <div class="hud-cell text-weight-bold bg-grey-9 text-grey-5">Team</div>
        <div class="hud-cell text-weight-bold text-uppercase ellipsis q-px-xs full-width">
          {{ visitingFullName }}
        </div>
        <div class="hud-cell text-weight-bold text-uppercase ellipsis q-px-xs full-width">
          {{ homeName }}
        </div>
      </div>

      <!-- NEW: EXPAND/CONTRACT TOGGLE -->
      <div
        class="col-auto column justify-center border-right bg-grey-10 cursor-pointer hover-highlight"
        style="width: 28px; height: 100%"
        @click="isScoreboardExpanded = !isScoreboardExpanded"
      >
        <q-icon
          :name="isScoreboardExpanded ? 'chevron_left' : 'chevron_right'"
          color="grey-5"
          size="sm"
          class="q-ma-auto"
        />
      </div>

      <!-- INNINGS (Dynamic) -->
      <!-- FIX: Changed 'col-grow' to 'col' to strictly lock width and allow the inner horizontal scroll -->
      <div class="col row no-wrap overflow-auto hide-scrollbar">
        <!-- Changed loop to visibleInnings and dynamic flex class -->
        <div
          v-for="inning in visibleInnings"
          :key="inning"
          class="column border-right transition-all"
          :class="isScoreboardExpanded ? 'flex-1' : 'col-12'"
        >
          <div
            class="hud-cell cursor-pointer hover-highlight"
            :class="
              inning === activeInning
                ? 'bg-primary text-white text-weight-bolder'
                : 'bg-grey-9 text-grey-5'
            "
            @click="jumpToInning(inning)"
          >
            {{ inning }}
          </div>

          <div
            class="hud-cell cursor-pointer hover-highlight relative-position"
            :class="{ 'active-half-inning': inning === activeInning && activeHalfInning === 'top' }"
          >
            {{ getScore('visitor', inning) }}
            <q-popup-proxy
              transition-show="scale"
              transition-hide="scale"
              anchor="center middle"
              self="center middle"
              :breakpoint="0"
            >
              <q-card class="bg-grey-9 text-white">
                <q-card-section class="row items-center no-wrap q-pa-sm">
                  <q-btn
                    round
                    dense
                    flat
                    color="negative"
                    icon="remove"
                    @click="adjustRun('visitor', inning, -1)"
                  />
                  <div class="q-px-md text-h6">{{ getScore('visitor', inning) || 0 }}</div>
                  <q-btn
                    round
                    dense
                    flat
                    color="positive"
                    icon="add"
                    @click="adjustRun('visitor', inning, 1)"
                  />
                </q-card-section>
              </q-card>
            </q-popup-proxy>
          </div>

          <div
            class="hud-cell cursor-pointer hover-highlight relative-position"
            :class="{
              'active-half-inning': inning === activeInning && activeHalfInning === 'bottom',
            }"
          >
            {{ getScore('home', inning) }}
            <q-popup-proxy
              transition-show="scale"
              transition-hide="scale"
              anchor="center middle"
              self="center middle"
              :breakpoint="0"
            >
              <q-card class="bg-grey-9 text-white">
                <q-card-section class="row items-center no-wrap q-pa-sm">
                  <q-btn
                    round
                    dense
                    flat
                    color="negative"
                    icon="remove"
                    @click="adjustRun('home', inning, -1)"
                  />
                  <div class="q-px-md text-h6">{{ getScore('home', inning) || 0 }}</div>
                  <q-btn
                    round
                    dense
                    flat
                    color="positive"
                    icon="add"
                    @click="adjustRun('home', inning, 1)"
                  />
                </q-card-section>
              </q-card>
            </q-popup-proxy>
          </div>
        </div>
      </div>

      <!-- RUNS TOTALS -->
      <div class="col-2 column border-left">
        <div class="hud-cell text-weight-bold bg-grey-9 text-warning">R</div>
        <div class="hud-cell text-weight-bold text-warning">{{ visitorRuns }}</div>
        <div class="hud-cell text-weight-bold text-warning">{{ homeRuns }}</div>
      </div>
    </div>

    <!-- UPDATED BOTTOM ROW -->
    <div class="bg-grey-10 text-grey-4 text-caption" style="border-top: 1px solid #444">
      <!-- Row 1: batter (left-aligned, full width available) -->
      <div class="row items-center q-px-sm q-pt-xs cursor-pointer" @click="showLineupMenu = true">
        <q-icon name="menu" size="xs" class="q-mr-xs" />
        <span class="text-accent text-uppercase text-weight-bold text-body2">
          AB: #{{ gameStore.currentBatter?.player?.uniformNumber || '-' }}
        </span>
        <span class="q-ml-xs text-grey-5">
          ({{ gameStore.currentBatter?.battingOrder || 1 }} of {{ gameStore.totalBatters }})
        </span>
      </div>

      <!-- LEFT SIDEBAR: LINEUP JUMP DIALOG -->
      <q-dialog v-model="showLineupMenu" position="left">
        <!-- FIX: Added 'overflow: hidden' to trap the outer scrollbar and 'col-auto' to the toolbar -->
        <q-card
          class="full-height column no-wrap bg-white"
          style="width: 300px; max-width: 85vw; overflow: hidden"
        >
          <q-toolbar class="bg-primary text-white col-auto shadow-2" style="z-index: 10">
            <q-toolbar-title
              class="text-subtitle1 text-weight-bold text-uppercase"
              style="letter-spacing: 0.5px"
            >
              Move to Lineup Spot
            </q-toolbar-title>
            <q-btn flat round dense icon="close" v-close-popup />
          </q-toolbar>

          <q-card-section class="col scroll q-pa-none">
            <q-list separator>
              <q-item
                v-for="(entry, index) in activeOffensiveLineup"
                :key="entry.player.id || index"
                clickable
                v-ripple
                v-close-popup
                class="q-py-md"
                :active="entry.player.id === gameStore.currentBatter?.player?.id"
                active-class="text-primary text-weight-bold bg-blue-1"
                @click="gameStore.jumpToLineupSpot(entry.player.id || 0)"
              >
                <q-item-section avatar style="min-width: 45px">
                  <q-avatar
                    :color="
                      entry.player.id === gameStore.currentBatter?.player?.id ? 'primary' : 'grey-3'
                    "
                    :text-color="
                      entry.player.id === gameStore.currentBatter?.player?.id ? 'white' : 'black'
                    "
                    size="md"
                    class="text-weight-bold shadow-1"
                  >
                    {{ entry.battingOrder }}
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-subtitle1">
                    #{{ entry.player.uniformNumber || '-' }} {{ entry.player.name }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>
      </q-dialog>

      <!-- CENTER: INNING & PITCHER -->
      <!-- Row 2: inning/pitcher (left) + summary (right) -->
      <div class="row items-center justify-between q-px-sm q-pb-xs">
        <div>
          {{ inningString }}
          <span
            class="text-accent text-uppercase cursor-pointer q-ml-xs"
            @click="showPitcherMenu = true"
          >
            P: #{{ gameStore.currentPitcher?.player?.uniformNumber || '-' }}
          </span>
          <span class="text-grey-5">({{ gameStore.currentPitcherPitchCount }})</span>
        </div>
        <div class="cursor-pointer text-info text-weight-bold" @click="showSummaryDialog = true">
          SUMMARY
        </div>
      </div>
    </div>

    <!-- LEFT SIDEBAR: PITCHER DIALOG -->
    <q-dialog v-model="showPitcherMenu" position="left">
      <!-- FIX: Added 'overflow: hidden' to trap the outer scrollbar and 'col-auto' to the toolbar -->
      <q-card
        class="full-height column no-wrap bg-white"
        style="width: 300px; max-width: 85vw; overflow: hidden"
      >
        <q-toolbar class="bg-primary text-white col-auto shadow-2" style="z-index: 10">
          <q-toolbar-title
            class="text-subtitle1 text-weight-bold text-uppercase"
            style="letter-spacing: 0.5px"
          >
            Select Pitcher
          </q-toolbar-title>
          <q-btn flat round dense icon="close" v-close-popup />
        </q-toolbar>
        <q-card-section class="col scroll q-pa-none">
          <q-list separator>
            <q-item
              v-for="(entry, index) in gameStore.activeDefensiveLineup"
              :key="entry.player.id || index"
              clickable
              v-close-popup
              :active="entry.player.id === gameStore.currentPitcher?.player?.id"
              active-class="text-primary text-weight-bold bg-blue-1"
              @click="gameStore.changePitcher(entry)"
            >
              <q-item-section>
                <q-item-label
                  >#{{ entry.player.uniformNumber || '-' }} {{ entry.player.name }}</q-item-label
                >
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog v-model="showSummaryDialog" position="bottom" full-width>
      <div style="max-width: 800px; margin: 0 auto; width: 100%">
        <q-toolbar
          class="bg-primary text-white"
          style="border-top-left-radius: 12px; border-top-right-radius: 12px"
        >
          <q-toolbar-title class="text-subtitle1 text-weight-bold">
            Game Log & Summary
          </q-toolbar-title>
          <q-btn flat round dense icon="close" v-close-popup />
        </q-toolbar>

        <GameSummary />
      </div>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useGameStore } from 'stores/game-store'

// NEW: Import the summary component! Make sure the path matches your project structure.
import GameSummary from 'components/GameSummary.vue'

const gameStore = useGameStore()

// NEW: Dialog state
const showSummaryDialog = ref(false)
const showLineupMenu = ref(false)
const showPitcherMenu = ref(false)

// NEW: Toggle state for the scoreboard
const isScoreboardExpanded = ref(false)

// NEW: Dynamically render either the current inning or the full spread
const visibleInnings = computed(() => {
  if (isScoreboardExpanded.value) {
    // Returns [1, 2, 3, 4, ... totalInnings]
    return Array.from({ length: totalInnings.value }, (_, i) => i + 1)
  }
  // Returns just the active inning: e.g., [4]
  return [activeInning.value]
})

// Grab the correct lineup based on who is currently hitting
const activeOffensiveLineup = computed(() => {
  return activeHalfInning.value === 'top' ? gameStore.visitingLineup : gameStore.homeLineup
})

// Formats the inning number into 'Top 1st', 'Bottom 2nd', etc.
const inningString = computed(() => {
  return gameStore.activeInningString
})

const visitingFullName = computed(() => gameStore.teamVisiting?.name || 'Away')
const homeName = computed(() => (gameStore.teamHome?.name || 'HOM').substring(0, 3))
const visitorRuns = computed(() => gameStore.visitorRuns)
const homeRuns = computed(() => gameStore.homeRuns)

// Show standard 9 innings, but expand dynamically if the game goes into extra innings
const totalInnings = computed(() => {
  const current = gameStore.activeGameSummary?.currentInning || 1
  return Math.max(9, current)
})

// Dynamically track which inning we are currently viewing in the app
const activeInning = computed(() => {
  return gameStore.viewedPlateAppearance?.inning || gameStore.activeGameSummary?.currentInning || 1
})

// Track whether the viewed plate appearance is in the top or bottom of the inning
const activeHalfInning = computed(() => {
  return (
    gameStore.viewedPlateAppearance?.halfInning ||
    gameStore.activeGameSummary?.currentHalfInning ||
    'top'
  )
})

function getScore(team: 'home' | 'visitor', inning: number) {
  const summary = gameStore.activeGameSummary
  if (!summary) return ''

  const arr = team === 'home' ? summary.homeScoreByInning : summary.visitingScoreByInning
  const score = arr[inning - 1]

  // If the inning hasn't occurred yet, leave the scoreboard cell cleanly blank
  return score !== undefined ? score : ''
}

function adjustRun(team: 'home' | 'visitor', inning: number, amount: number) {
  const halfInning = team === 'home' ? 'bottom' : 'top'
  void gameStore.adjustScore(halfInning, inning - 1, amount, 'Manual scoreboard adjustment')
}

function jumpToInning(inning: number) {
  const index = gameStore.plateAppearances.findIndex((pa) => pa.inning === inning)

  if (index !== -1) {
    gameStore.currentPAIndex = index
  } else {
    console.warn(`Cannot jump to inning ${inning}. No Plate Appearances exist yet.`)
  }
}
</script>

<style scoped>
/* Keep existing styles intact */
.scoreboard-wrapper {
  font-family: monospace;
  font-size: 1.1rem;
}
.hud-cell {
  padding: 4px 0;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #444;
}
.hud-cell:last-child {
  border-bottom: none;
}
.active-half-inning {
  box-shadow: inset 0 0 0 3px var(--q-primary);
  z-index: 1;
}
.border-right {
  border-right: 1px solid #444;
}
.border-left {
  border-left: 1px solid #444;
}
.flex-1 {
  flex: 1;
  min-width: 36px;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hover-highlight:active {
  background-color: #555;
}
</style>
